import { useEffect, useState } from 'react';
import {
    Baby,
    Bath,
    Car,
    ChevronDown,
    ChevronUp,
    Coffee,
    Flame,
    Leaf,
    Mountain,
    PawPrint,
    ShieldCheck,
    Snowflake,
    Sparkles,
    Tent,
    Trees,
    Tv,
    Utensils,
    Waves,
    Wifi
} from 'lucide-react';

import styles from './CampFilters.module.css';

/*
 * Left sidebar for the camp / stay search page.
 *
 * sections = the filters support added (Support → Dropdowns/Filters →
 * Filter, type Camp) — the same for every search. Loaded once from
 * /info/filter/dropdowns/camp:
 *   [{ heading: 'Room Type', field: 'unitTypes',
 *      options: [{ value: 'tent', label: 'Tent' }, ...] }, ...]
 *
 * facets = from the search (first page) — just numbers:
 *   { price: { min, max }, counts: { unitTypes: { tent: 24 }, ... } }
 *
 * value    = { minPrice, maxPrice, unitTypes: [], propertyTypes: [], amenities: [] }
 * onChange = called with the new value (the page saves it in the URL)
 */

const AMENITIES_SHOWN = 6;

// "entire_property" -> "Entire property"
export const prettyLabel = (text = '') => {
    const t = String(text).replace(/_/g, ' ').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
};

// Icon by amenity name (first match), default ✨
const AMENITY_ICONS = [
    [/pool|swim/i, Waves],
    [/bonfire|campfire|fire/i, Flame],
    [/wifi|internet/i, Wifi],
    [/meal|food|breakfast|lunch|dinner|restaurant|kitchen/i, Utensils],
    [/pet|dog/i, PawPrint],
    [/parking|car/i, Car],
    [/trek|hik|mountain|view/i, Mountain],
    [/tea|coffee/i, Coffee],
    [/garden|forest|nature|plantation/i, Trees],
    [/tv|television/i, Tv],
    [/\bac\b|air condition/i, Snowflake],
    [/bath|shower|hot water|geyser/i, Bath],
    [/tent|camp/i, Tent],
    [/kid|child|family/i, Baby],
    [/first aid|security|cctv|safe/i, ShieldCheck],
    [/organic|eco/i, Leaf]
];
const amenityIcon = (name) => (AMENITY_ICONS.find(([re]) => re.test(name)) || [null, Sparkles])[1];

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function CheckList({ title, options = [], selected = [], onToggle, withIcons = false, limit }) {
    const [expanded, setExpanded] = useState(false);
    if (!options.length) return null;

    const shown = limit && !expanded ? options.slice(0, limit) : options;

    return (
        <section className={styles.section}>
            <h4 className={styles.sectionTitle}>{title}</h4>
            <ul className={styles.list}>
                {shown.map(({ value, label, count }) => {
                    const Icon = withIcons ? amenityIcon(value) : null;
                    const checked = selected.includes(value);
                    return (
                        <li key={value}>
                            <label className={`${styles.option} ${checked ? styles.checked : ''}`}>
                                <input type='checkbox' checked={checked} onChange={() => onToggle(value)} />
                                {Icon && <Icon size={15} strokeWidth={1.8} className={styles.optionIcon} />}
                                <span className={styles.optionLabel}>{label || prettyLabel(value)}</span>
                                {count != null && <span className={styles.count}>{count}</span>}
                            </label>
                        </li>
                    );
                })}
            </ul>
            {limit && options.length > limit && (
                <button type='button' className={styles.showMore} onClick={() => setExpanded((v) => !v)}>
                    {expanded ? 'Show less' : `Show more (${options.length - limit})`}
                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
            )}
        </section>
    );
}

// Two sliders on one track: min and max price per night
function PriceRange({ min, max, value, onChange }) {
    const step = max - min > 5000 ? 100 : 50;
    const [low, setLow] = useState(value.minPrice ?? min);
    const [high, setHigh] = useState(value.maxPrice ?? max);

    // follow the URL / new search
    useEffect(() => {
        setLow(value.minPrice ?? min);
        setHigh(value.maxPrice ?? max);
    }, [value.minPrice, value.maxPrice, min, max]);

    if (!(max > min)) return null;

    const pct = (v) => ((v - min) / (max - min)) * 100;

    // save when the user lets go of the slider
    const commit = () =>
        onChange({
            minPrice: low > min ? low : null,
            maxPrice: high < max ? high : null
        });

    return (
        <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Price per night</h4>
            <div className={styles.range}>
                <div className={styles.track} />
                <div className={styles.trackFill} style={{ left: `${pct(low)}%`, right: `${100 - pct(high)}%` }} />
                <input
                    type='range'
                    aria-label='Minimum price'
                    min={min}
                    max={max}
                    step={step}
                    value={low}
                    onChange={(e) => setLow(Math.min(Number(e.target.value), high - step))}
                    onMouseUp={commit}
                    onTouchEnd={commit}
                    onKeyUp={commit}
                />
                <input
                    type='range'
                    aria-label='Maximum price'
                    min={min}
                    max={max}
                    step={step}
                    value={high}
                    onChange={(e) => setHigh(Math.max(Number(e.target.value), low + step))}
                    onMouseUp={commit}
                    onTouchEnd={commit}
                    onKeyUp={commit}
                />
            </div>
            <div className={styles.rangeValues}>
                <span>{inr(low)}</span>
                <span>{inr(high)}</span>
            </div>
        </section>
    );
}

const lower = (v) =>
    String(v || '')
        .trim()
        .toLowerCase();

// Price slider range when the search hasn't answered yet (same as backend)
const DEFAULT_PRICE = { min: 0, max: 20000 };

export default function CampFilters({ sections, facets, value, onChange, className = '' }) {
    if (!sections) return <aside className={`${styles.card} ${styles.loading} ${className}`} />;

    const counts = facets?.counts || {};
    const price = facets?.price || DEFAULT_PRICE;

    const toggle = (key) => (item) => {
        const current = value[key] || [];
        const next = current.includes(item) ? current.filter((v) => v !== item) : [...current, item];
        onChange({ [key]: next });
    };

    const hasAny =
        value.minPrice != null ||
        value.maxPrice != null ||
        value.unitTypes?.length ||
        value.propertyTypes?.length ||
        value.amenities?.length;

    return (
        <aside className={`${styles.card} ${className}`}>
            <div className={styles.header}>
                <h3>Filters</h3>
                {hasAny ? (
                    <button
                        type='button'
                        className={styles.clear}
                        onClick={() =>
                            onChange({
                                minPrice: null,
                                maxPrice: null,
                                unitTypes: [],
                                propertyTypes: [],
                                amenities: []
                            })
                        }
                    >
                        Clear all
                    </button>
                ) : null}
            </div>

            <PriceRange min={price.min} max={price.max} value={value} onChange={onChange} />

            {/* One section per filter added by support (same for every search) */}
            {sections.map((section) => (
                <CheckList
                    key={section._id || section.heading}
                    title={section.heading}
                    options={section.options.map((o) => ({
                        ...o,
                        // how many stays in this place have it (null until the search answers)
                        count: facets ? counts[section.field]?.[lower(o.value)] || 0 : null
                    }))}
                    selected={value[section.field] || []}
                    onToggle={toggle(section.field)}
                    withIcons={section.field === 'amenities'}
                    limit={section.field === 'amenities' ? AMENITIES_SHOWN : undefined}
                />
            ))}
        </aside>
    );
}
